import { GraphQLError } from "graphql";
import isEmpty from "is-empty";
import React, { ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  checkSessionActivatedQuery,
  getNeobookingTranslationsQuery,
  getPartnerQuery,
  getSalesChannelModeQuery,
  getSessionQuery,
} from "rsl-api-106/lib/queries";
import {
  Language,
  Query,
  QueryCheckSessionActivatedArgs,
  QueryGetNeobookingTranslationsArgs,
  QueryGetPartnerArgs,
  QueryGetSalesChannelModeArgs,
  QueryGetSessionArgs,
  Service,
  Translation,
} from "rsl-api-106/lib/types";

import { useApolloClient, useQuery } from "@apollo/client";
import Alert from "@components/_Layout/Alert";
import BugReport from "@components/_Layout/BugReport/BugReport";
import LoadingScreen from "@components/_Layout/Loaders/LoadingScreen";
import { SessionContext } from "@contexts";
import { useConfig } from "@hooks";
import {
  getFavoritesEtabs,
  getFavoritesProposals,
  getStorageKeyByMode,
  showFavoritesEtab,
  showFavoritesProposal,
} from "@utils/favorites";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@utils/localStorage";
import { initGTM } from "@utils/tracking/gtm";

import { FavoriteEtabType, FavoriteProposalType, Funnel_Type } from "../types";

type SessionProviderProps = {
  children: ReactNode;
  funnel_type?: Funnel_Type;
  showLoader?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_contact_support", //Contacter le support
  "LANG_neobooking_loading_session_in_progress", //Chargement de la session en cours...
  "LANG_neobooking_please_wait", //Veuillez patienter quelques instants
  "LANG_neobooking_loading_session_error_title", //Erreur chargement session
  "LANG_neobooking_loading_session_error_description", //Une erreur est survenue lors de la récupération de la Session avec l'identifiant et le mot de passe.
  "LANG_neobooking_loading_session_error_content", //Vérifier que vous avez bien configuré le parametrage de votre application ou contacter le support pour plus d'informations.
  "LANG_neobooking_oups", //Oups
  "LANG_neobooking_partner_advantage_code", //Mon code avantage partenaire
  "LANG_neobooking_partner_enter_advantage_code", //Inscrivez votre code partenaire pour continuer sur la réservation en ligne.
  "LANG_neobooking_partner_contact_for_advantage", //Contactez l’organisme auquel vous êtes affilié pour connaitre vos avantages.
];

const INIT_SalesChannelMode = true;

export const SessionProvider: React.FC<SessionProviderProps> = ({
  funnel_type = "reservation",
  showLoader = true,
  children,
}) => {
  const getLanguage = () => {
    let _lang = searchParams.get("lang");
    if (
      searchParams.get("webuser") === "webEN" ||
      searchParams.get("webuser") === "web_en_b2b"
    ) {
      _lang = "AN";
    } else if (
      searchParams.get("wu") === "webEN" ||
      searchParams.get("wu") === "web_en_b2b"
    ) {
      _lang = "AN";
    }
    return _lang ?? "FR";
  };

  /* Hooks */
  const client = useApolloClient();
  const { gtm } = useConfig();
  const gtmId = !isEmpty(gtm) ? gtm : process.env.REACT_APP_GTM_TRACKING_ID!!;
  const [searchParams, setSearchParams] = useSearchParams();

  /* State  */
  const [sessionId, setSessionId] = useState<string | null>(
    searchParams.get("session") ?? getLocalStorageItem("sessionId")
  );
  const [sessionLoaded, setSessionLoaded] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<number | null>(null);
  const [partner, setPartner] = useState<Service | null>(null);
  const [error, setError] = useState<GraphQLError | null>(null);

  /* Favorites States */
  const [favoriteProposals, setFavoriteProposals] = useState<
    FavoriteProposalType[]
  >([]);
  const [favoriteEtabs, setFavoriteEtabs] = useState<FavoriteEtabType[]>([]);

  /* Lang States */
  const [lang, setLang] = useState<string>(getLanguage());
  const [languagesVars, setLanguagesVars] = useState<Translation[]>([]);

  const hasWebuserChange = () => {
    const localWebuser = getLocalStorageItem("webuser");
    let webuserChange = false;

    //Si on a un webuser localStorage et que celui-ci !== a celui du param, alors webuserChange = true
    if (
      localWebuser &&
      localWebuser.toUpperCase() !==
        (searchParams.get("webuser")
          ? searchParams.get("webuser")?.toUpperCase()
          : searchParams.get("wu")
          ? searchParams.get("wu")?.toUpperCase()
          : process.env.REACT_APP_WEBUSER?.toUpperCase())
    ) {
      webuserChange = true;
    }
    return webuserChange;
  };

  /* useQuery */
  const { refetch: refetchCheckSessionActivated } = useQuery<
    Query,
    QueryCheckSessionActivatedArgs
  >(checkSessionActivatedQuery(), {
    skip: true,
    variables: {
      session: { name: sessionId },
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (
        data.checkSessionActivated &&
        data.checkSessionActivated.activated &&
        data.checkSessionActivated.activated
      ) {
        setSessionId(sessionId as string);
        setLocalStorageItem("sessionId", sessionId as string);
        //setSessionLoaded(true);

        //Lors de la récupération de la session, on set les propositions & etabs en favoris
        if (showFavoritesEtab()) {
          setFavoriteEtabs(getFavoritesEtabs(sessionId as string));
        }
        if (showFavoritesProposal()) {
          setFavoriteProposals(getFavoritesProposals(sessionId as string));
        }

        if (INIT_SalesChannelMode) {
          refetchGetSalesChannelMode();
        } else {
          setSessionLoaded(true);
        }
      } else {
        refetchGetSession();
      }
    },
    onError: (error) => {
      refetchGetSession();
    },
  });

  const { refetch: refetchGetSession } = useQuery<Query, QueryGetSessionArgs>(
    getSessionQuery(),
    {
      skip: true,
      variables: {
        input: {
          username:
            searchParams.get("webuser") ??
            searchParams.get("wu") ??
            process.env.REACT_APP_WEBUSER!!,
        },
      },
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data.getSession && data.getSession.name) {
          setSessionId(data.getSession.name);
          setLocalStorageItem("sessionId", data.getSession.name);
          setLocalStorageItem(
            "webuser",
            searchParams.get("webuser") ??
              searchParams.get("wu") ??
              process.env.REACT_APP_WEBUSER!!
          );
          //setSessionLoaded(true);
          setError(null);

          //Lors de l'initialisation de la session, on supprime les propositions & etabs en favoris
          if (showFavoritesEtab()) {
            removeLocalStorageItem(getStorageKeyByMode("etab"));
          }
          if (showFavoritesProposal()) {
            removeLocalStorageItem(getStorageKeyByMode("proposal"));
          }

          //Lors qu'on génére une nouvelle session, on supprime le localStorage code_fidelite
          removeLocalStorageItem("code_fidelite");

          if (INIT_SalesChannelMode) {
            refetchGetSalesChannelMode();
          } else {
            setSessionLoaded(true);
          }
        }
      },
      onError: (error) => {
        setSessionId(null);
        removeLocalStorageItem("sessionId");
        setSessionLoaded(true);
        setError(error.graphQLErrors[0]);
      },
    }
  );

  const { refetch: refetchGetPartner } = useQuery<Query, QueryGetPartnerArgs>(
    getPartnerQuery(),
    {
      skip: true,
      variables: {
        session: { name: sessionId },
      },
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data.getPartner && data.getPartner.service) {
          setPartner(data.getPartner.service);
        }
      },
    }
  );

  const { refetch: refetchGetSalesChannelMode } = useQuery<
    Query,
    QueryGetSalesChannelModeArgs
  >(getSalesChannelModeQuery(), {
    skip: true,
    variables: {
      session: { name: sessionId },
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (
        data.getSalesChannelMode &&
        data.getSalesChannelMode.mode &&
        data.getSalesChannelMode.mode >= 0
      ) {
        setAuthMode(data.getSalesChannelMode.mode);
        initPartner(data.getSalesChannelMode.mode);
      }
      setSessionLoaded(true);
    },
    onError: (error) => {
      setError(error.graphQLErrors[0]);
    },
  });

  useEffect(() => {
    //Init GTM Tags at App Start
    initGTM(gtmId);

    //Si pas de sessionId, on en génère une
    if (!sessionId || hasWebuserChange()) {
      refetchGetSession();
    } else {
      //Sinon on check si le token de session est valide
      refetchCheckSessionActivated();
    }

    fetchLang();
  }, []);

  useEffect(() => {
    setLocalStorageItem("authMode", authMode?.toString() ?? "0");
  }, [authMode]);

  const fetchLang = (callback?: (response: Translation[]) => void) => {
    const path = "/varlang/cache.json";
    const languagesVarsUri =
      process.env.NODE_ENV === "development"
        ? path
        : process.env.REACT_APP_NEOBOOKING_URI + path;

    fetch(languagesVarsUri)
      .then(async (response) => {
        const result = (await response.json()) as Language[];
        let _lang = lang;
        if (searchParams.get("webuser") === "webEN") {
          _lang = "AN";
        } else if (searchParams.get("wu") === "webEN") {
          _lang = "AN";
        }
        const l = result.find(
          (x) => x.code?.toUpperCase() === _lang.toUpperCase()
        );
        if (l) {
          setLanguagesVars(l.languageVars as Translation[]);
          callback && callback(l.languageVars as Translation[]);
        } else {
          setLanguagesVars(result[0].languageVars as Translation[]);
          callback && callback(result[0].languageVars as Translation[]);
          setLang(result[0]?.code!!);
        }
      })
      .catch((err) => {
        fetchGraphQL((response) => {
          callback && callback(response as Translation[]);
        });
      });
  };

  const fetchGraphQL = (callback?: (response: Translation[]) => void) => {
    client
      .query<Query, QueryGetNeobookingTranslationsArgs>({
        query: getNeobookingTranslationsQuery(),
        variables: {
          session: { name: sessionId!! },
          input: {
            languages: process.env.REACT_APP_LANG?.split(",") ?? ["FR"],
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        if (response.data.getNeobookingTranslations?.translations) {
          console.log("fetchGraphQL LanguagesVars");
          let _lang = lang;
          if (searchParams.get("webuser") === "webEN") {
            _lang = "AN";
          } else if (searchParams.get("wu") === "webEN") {
            _lang = "AN";
          }
          const rl = response.data.getNeobookingTranslations?.translations.find(
            (x) => x?.code?.toUpperCase() === _lang.toUpperCase()
          );
          if (rl) {
            console.log("on a trouvé la lang system: " + _lang.toUpperCase());
            console.log(rl);
            setLanguagesVars(rl.languageVars as Translation[]);
          } else {
            setLanguagesVars(
              (response?.data?.getNeobookingTranslations.translations[0]
                ?.languageVars as Translation[]) ?? []
            );
            setLang(
              response?.data?.getNeobookingTranslations.translations[0]?.code ??
                getLanguage()
            );
          }
        }
      });
  };

  const getLanguageVarValue = (code: string): string => {
    const vars = [...languagesVars];
    let lv = "";
    if (vars.length <= 0) {
      fetchLang((response) => {
        const langValue = response.find((x) => x.code === code);
        lv = code;
        if (langValue) {
          lv = langValue?.value ?? code;
        } else {
          console.log("on a pas la traduction de la clé: " + code);
        }
      });
    } else {
      const langValue = vars.find((x) => x.code === code);
      lv = code;
      if (langValue) {
        lv = langValue?.value ?? code;
      } else {
        console.log("on a pas la traduction de la clé: " + code);
      }
    }
    return lv;
  };

  const initPartner = (authMode: number) => {
    if (sessionId && [3, 6].includes(authMode as number)) {
      refetchGetPartner();
    } else {
      setSessionLoaded(true);
    }
  };

  if (!sessionLoaded) {
    return <LoadingScreen />;
  }

  return (
    <SessionContext.Provider
      value={{
        sessionId,
        setSessionId,
        authMode,
        partner,
        setPartner,
        favoriteProposals,
        lang,
        funnel_type: authMode === 2 ? "b2b" : funnel_type,
        getLanguageVarValue,
        languagesVars,
        setFavoriteProposals,
        favoriteEtabs,
        setFavoriteEtabs,
      }}
    >
      <React.Fragment>
        <>
          {children}
          {process.env.REACT_APP_BUGREPORT === JSON.stringify(true) && (
            <BugReport></BugReport>
          )}
        </>
      </React.Fragment>
    </SessionContext.Provider>
  );
};
